/**
 * PromptSlashDropdown — the `/` typeahead's list (2026-09-17).
 *
 * Purely presentational, the `ThreadMentionAutocompleteDropdown` shape: the
 * controller (`usePromptSlashAutocomplete`) owns detection, ranking and the
 * insert; this renders the rows and forwards hover/select. Fixed-position and
 * body-portaled so it floats just above the composer whatever the docked chat's
 * transformed stacking context is doing — the chat pane carries
 * `backdrop-filter` and `container-type`, either of which would trap a fixed
 * child inside it at ANY z-index.
 *
 * Three portal rules, all load-bearing and two of them written in production
 * incidents: `BODY_PORTAL_Z_INDEX` from the one ladder (never a literal — the
 * overlay gate takes its denominator from every `createPortal` site under
 * `src/`); `data-prest0n-portal="true"` so a click inside it does not dismiss
 * the chat, and spelled with its VALUE because the guard's selector is
 * `[data-prest0n-portal="true"]` and an empty attribute silently never matched;
 * and inert by contract — `null` unless open, anchored and non-empty.
 *
 * `suppressed` is the collision rule, and it is answered HERE at render time
 * rather than in controller state, which would be one render stale at exactly
 * the wrong moment: when a mention dropdown is legitimately open at the same
 * instant (`/intro @ma`), the older surface paints and this one stands down.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { BODY_PORTAL_Z_INDEX } from '../../styles/layers';
import type { SlashRow } from './usePromptSlashAutocomplete';
import { currentViewport, dropUpPlacement } from './dropUpPlacement';

export interface PromptSlashAnchorRect {
  left: number;
  top: number;
  width: number;
}

export interface PromptSlashDropdownProps {
  open: boolean;
  rows: readonly SlashRow[];
  activeIndex: number;
  anchorRect: PromptSlashAnchorRect | null;
  /** The mention dropdown is painting; this one stands down for this render. */
  suppressed?: boolean;
  onSelect: (row: SlashRow) => void;
  onHover: (index: number) => void;
  /** The list's label and the team mark, in the viewer's language. */
  label: string;
  teamMark: string;
  blanksCount: (n: number) => string;
}

const cardStyle: React.CSSProperties = {
  position: 'fixed',
  background: 'var(--surface, #fff)',
  color: 'var(--text, #16181d)',
  border: '1px solid rgba(127,127,127,0.22)',
  borderRadius: 12,
  boxShadow: '0 10px 34px rgba(0,0,0,0.22)',
  maxHeight: 260,
  overflowY: 'auto',
  padding: '6px 0',
  zIndex: BODY_PORTAL_Z_INDEX,
};

const rowBase: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '6px 14px',
  border: 0,
  background: 'transparent',
  font: 'inherit',
  cursor: 'pointer',
};

const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const metaStyle: React.CSSProperties = { fontSize: 11, opacity: 0.6 };

const markStyle: React.CSSProperties = {
  display: 'inline-block',
  marginRight: 5,
  padding: '0 5px',
  fontSize: 9.5,
  fontWeight: 600,
  borderRadius: 999,
  background: 'rgba(127,127,127,0.16)',
  verticalAlign: 1,
};

export const PromptSlashDropdown: React.FC<PromptSlashDropdownProps> = ({
  open, rows, activeIndex, anchorRect, suppressed, onSelect, onHover, label, teamMark, blanksCount,
}) => {
  if (suppressed || !open || !anchorRect || rows.length === 0) return null;
  if (typeof document === 'undefined' || !document.body) return null;

  // Clamped against the LIVE viewport on BOTH edges — the card's bottom sits
  // just above the composer's top so it grows upward and never covers what is
  // being typed, and its right edge can no longer leave the screen. This
  // component re-renders on every keystroke that changes the query, so reading
  // the viewport here is enough; the panel next door, which can sit open across
  // a window resize, subscribes with `useDropUpViewport` instead.
  const placement = dropUpPlacement({
    anchor: anchorRect,
    viewport: currentViewport(),
    preferredWidth: Math.min(anchorRect.width, 420),
    minWidth: 220,
  });
  const style: React.CSSProperties = {
    ...cardStyle,
    left: placement.left,
    bottom: placement.bottom,
    width: placement.width,
  };

  const content = (
    <div
      style={style}
      role="listbox"
      aria-label={label}
      data-prest0n-portal="true"
      data-prompt-slash-dropdown=""
    >
      {rows.map((row, index) => (
        <button
          key={`${row.shelf}:${row.id}`}
          type="button"
          role="option"
          aria-selected={index === activeIndex}
          data-prompt-slash-row={row.id}
          data-prompt-slash-shelf={row.shelf}
          style={{
            ...rowBase,
            background: index === activeIndex ? 'rgba(127,127,127,0.12)' : 'transparent',
          }}
          onMouseEnter={() => onHover(index)}
          // mousedown, not click: the composer must not lose its selection
          // before the insert reads where the caret was.
          onMouseDown={(e) => { e.preventDefault(); onSelect(row); }}
        >
          <span style={titleStyle}>
            {row.shelf === 'team' && <span style={markStyle}>{teamMark}</span>}
            {row.title}
          </span>
          <span style={metaStyle}>
            {[row.category, row.blanks.length > 0 ? blanksCount(row.blanks.length) : '']
              .filter(Boolean).join(' · ')}
          </span>
        </button>
      ))}
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default PromptSlashDropdown;
